import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./AddStock.scss";
import AsyncSelect from "react-select/async";
import { Form, Button, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { Card, CardBody, Col, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { addStock, itemsMaster } from "./StockService";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa6";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { useCurrency } from "../../_helper/CurrencyContext";
import InfoIcon from "./components/InfoIcon";
import { COLUMN_INFO } from "./stoctTableInfo";

const PurchaseFromPreOrder = () => {
    const { t } = useTranslation();
    const { getCurrencySymbol } = useCurrency();
    const { loading, setLoading } = useLoader();
    const { pharmacyPOS } = usePharmacy();
    const navigate = useNavigate();
    const location = useLocation();
    const disableScrollProps = useDisableScrollOnFocus();

    // Get preorder data from navigation state
    const { preOrderData } = location.state || {};

    // ============ FORM STATE ============
    const [supplier, setSupplier] = useState(null);
    const [items, setItems] = useState([]);
    const [totalPaid, setTotalPaid] = useState("");
    const [note, setNote] = useState("");
    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
    const [currency, setCurrency] = useState("INR");
    const [errors, setErrors] = useState({});
    const [isTotalPaidModified, setIsTotalPaidModified] = useState(false);

    // ============ SEARCH STATE ============
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMedicine, setSelectedMedicine] = useState(null);

    // ============ TAX SYSTEM STATE ============
    const [pharmacyStateCode, setPharmacyStateCode] = useState(pharmacyPOS?.stateCode || "");
    const [supplierStateCode, setSupplierStateCode] = useState("");
    const [posTaxType] = useState(pharmacyPOS?.taxType || "gst");
    const [isSameState, setIsSameState] = useState(true);

    // ============ TOTALS STATE ============
    const [netTotal, setNetTotal] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [taxSummary, setTaxSummary] = useState({
        CGST: 0,
        SGST: 0,
        IGST: 0,
        VAT: 0,
        totalTax: 0,
    });

    // ============ INITIALIZE FROM PREORDER DATA ============
    useEffect(() => {
        if (!preOrderData) {
            toast.warning(t("No pre-order data found"));
            navigate(-1);
            return;
        }

        // Set supplier info from pre-order
        const supplierInfo = preOrderData.supplierInfo;
        const supplierObj = {
            value: preOrderData.supplierId,
            label: supplierInfo?.companyName || "",
            raw: supplierInfo
        };

        setSupplier(supplierObj);
        setSupplierStateCode(supplierInfo?.stateCode || "");

        // Determine tax type based on state codes
        const newIsSameState = determineTaxType(pharmacyStateCode, supplierInfo?.stateCode);
        setIsSameState(newIsSameState);

        // Fetch items from master for each pre-order item
        fetchPreOrderItems(preOrderData.preorderData || []);

        // Auto-set total paid to 0 (will be calculated later)
        setTotalPaid("0.00");

    }, [preOrderData, pharmacyStateCode, navigate, t]);

    // ============ FETCH ITEMS FROM MASTER ============
    const fetchPreOrderItems = async (preOrderItems) => {
        try {
            setLoading(true);
            const itemPromises = preOrderItems.map(async (preItem) => {
                try {
                    // Fetch item details from master using itemId
                    const response = await itemsMaster({
                        search: preItem.itemId,
                        clinicId: pharmacyPOS?.hospital_id
                    });

                    const masterItems = response.data.data || [];
                    const masterItem = masterItems.find(item => item._id === preItem.itemId);

                    if (masterItem) {
                        return {
                            ...masterItem,
                            // Use pre-order quantity, fallback to 1
                            stkQuantity: preItem.quantity || 1,
                            itemQuantity: (preItem.quantity || 1) * (masterItem.packageQuantity || 1),
                            unitPrice: masterItem.regularPrice || 0,
                            unitSalePrice: 0,
                            batchNo: "",
                            manufacturingDate: "",
                            expiryDate: "",
                            discount: 0,
                            taxPercentage: 0,
                            taxAmount: 0,
                            CGSTPercent: 0, CGSTAmount: 0,
                            SGSTPercent: 0, SGSTAmount: 0,
                            IGSTPercent: 0, IGSTAmount: 0,
                            VATPercent: 0, VATAmount: 0,
                        };
                    } else {
                        // If item not found in master, create basic item from pre-order data
                        return {
                            _id: preItem.itemId,
                            itemId: preItem.itemId,
                            code: preItem.code || `ITEM-${Date.now()}`,
                            brandName: preItem.brandName || "",
                            saltName: preItem.saltName || "",
                            manufacturer: preItem.manufacturer || "",
                            package: preItem.package || "",
                            packageQuantity: 1,
                            isOTC: false,
                            regularPrice: 0,
                            stkQuantity: preItem.quantity || 1,
                            itemQuantity: preItem.quantity || 1,
                            unitPrice: 0,
                            unitSalePrice: 0,
                            batchNo: "",
                            manufacturingDate: "",
                            expiryDate: "",
                            discount: 0,
                            taxPercentage: 0,
                            taxAmount: 0,
                            CGSTPercent: 0, CGSTAmount: 0,
                            SGSTPercent: 0, SGSTAmount: 0,
                            IGSTPercent: 0, IGSTAmount: 0,
                            VATPercent: 0, VATAmount: 0,
                        };
                    }
                } catch (error) {
                    console.error(`Error fetching item ${preItem.itemId}:`, error);
                    // Return basic item if fetch fails
                    return {
                        _id: preItem.itemId,
                        itemId: preItem.itemId,
                        code: preItem.code || `ITEM-${Date.now()}`,
                        brandName: preItem.brandName || "",
                        saltName: preItem.saltName || "",
                        manufacturer: preItem.manufacturer || "",
                        package: preItem.package || "",
                        packageQuantity: 1,
                        isOTC: false,
                        regularPrice: 0,
                        stkQuantity: preItem.quantity || 1,
                        itemQuantity: preItem.quantity || 1,
                        unitPrice: 0,
                        unitSalePrice: 0,
                        batchNo: "",
                        manufacturingDate: "",
                        expiryDate: "",
                        discount: 0,
                        taxPercentage: 0,
                        taxAmount: 0,
                        CGSTPercent: 0, CGSTAmount: 0,
                        SGSTPercent: 0, SGSTAmount: 0,
                        IGSTPercent: 0, IGSTAmount: 0,
                        VATPercent: 0, VATAmount: 0,
                    };
                }
            });

            const fetchedItems = await Promise.all(itemPromises);
            setItems(fetchedItems);

        } catch (error) {
            console.error("Error fetching pre-order items:", error);
            toast.warning(t("Failed to load pre-order items"));
        } finally {
            setLoading(false);
        }
    };

    // ============ LOAD MEDICINES FOR ADDITIONAL ITEMS ============
    const loadMedicines = async (inputValue) => {
        if (!inputValue) return [];
        try {
            setLoading(true);
            const res = await itemsMaster({
                search: inputValue,
                clinicId: pharmacyPOS?.hospital_id
            });

            return (res.data.data || []).map(m => ({
                value: m._id,
                label: `${m.saltName} ${!m.isOTC ? `[${m.brandName}]` : ""}`,
                raw: m
            }));
        } catch (err) {
            console.error("Error loading medicines:", err);
            return [];
        } finally {
            setLoading(false);
        }
    };

    // ============ ADD NEW ITEM FROM SEARCH ============
    const handleSelectMedicine = (option) => {
        if (!option) return;
        const med = option.raw;

        // Check if item already exists
        if (items.find(i => i._id === med._id)) {
            toast.warning(t("Item already added"));
            setSelectedMedicine(null);
            return;
        }

        const newItem = {
            ...med,
            stkQuantity: 1,
            itemQuantity: med.packageQuantity || 1,
            unitPrice: med.regularPrice || 0,
            unitSalePrice: 0,
            batchNo: "",
            manufacturingDate: "",
            expiryDate: "",
            discount: 0,
            taxPercentage: 0,
            taxAmount: 0,
            CGSTPercent: 0, CGSTAmount: 0,
            SGSTPercent: 0, SGSTAmount: 0,
            IGSTPercent: 0, IGSTAmount: 0,
            VATPercent: 0, VATAmount: 0,
        };

        setItems(prev => [...prev, newItem]);
        setSelectedMedicine(null);
        setSearchQuery('');
    };

    // ============ HELPER FUNCTIONS (SAME AS AddStock) ============
    const determineTaxType = (pharmacyCode, supplierCode) => {
        if (!pharmacyCode || pharmacyCode.trim() === "" || !supplierCode || supplierCode.trim() === "") {
            return true;
        }
        return pharmacyCode === supplierCode;
    };

    const updateItem = (_id, field, value) => {
        setItems(prev => prev.map(item =>
            item._id === _id ? { ...item, [field]: value } : item
        ));
    };

    const updatePackageQty = (_id, value) => {
        const num = Math.max(1, Number(value) || 1);
        setItems(prev => prev.map(item =>
            item._id === _id
                ? { ...item, stkQuantity: num, itemQuantity: num * (item.packageQuantity || 1) }
                : item
        ));
    };

    const updateBatchNo = (_id, value) => {
        setItems((prev) =>
            prev.map((item) =>
                item._id === _id ? { ...item, batchNo: value } : item
            )
        );
    };

    const updateManufacturingDate = (_id, value) => {
        setItems((prev) =>
            prev.map((item) =>
                item._id === _id ? { ...item, manufacturingDate: value } : item
            )
        );
    };

    const updateExpiryDate = (_id, value) => {
        setItems((prev) =>
            prev.map((item) =>
                item._id === _id ? { ...item, expiryDate: value } : item
            )
        );
    };

    const removeItem = (_id) => {
        // Don't allow removing all items
        if (items.length <= 1) {
            toast.warning(t("At least one item is required"));
            return;
        }
        setItems(prev => prev.filter(i => i._id !== _id));
    };

    // ============ CALCULATIONS (SAME AS AddStock) ============
    const calculateSubtotal = (item) => {
        const base = item.stkQuantity * item.unitPrice;
        const disc = Number(item.discount) || 0;
        return (base - disc).toFixed(2);
    };

    const calculateItemTotalWithTax = (item) => {
        const subtotal = Number(calculateSubtotal(item)) || 0;
        const tax = item.taxAmount || 0;
        return (subtotal + tax).toFixed(2);
    };

    const recalculateAll = useCallback(() => {
        if (items.length === 0) {
            setNetTotal(0);
            setTotalPrice(0);
            setTaxSummary({ CGST: 0, SGST: 0, IGST: 0, VAT: 0, totalTax: 0 });
            if (!isTotalPaidModified) setTotalPaid("");
            return;
        }

        let subtotal = 0;
        let totalCGST = 0, totalSGST = 0, totalIGST = 0, totalVAT = 0;

        const useIntraState = determineTaxType(pharmacyStateCode, supplierStateCode);
        setIsSameState(useIntraState);

        const updated = items.map(item => {
            const pkgQty = Number(item.stkQuantity) || 0;
            const price = Number(item.unitPrice) || 0;
            const discount = Number(item.discount) || 0;

            const base = pkgQty * price;
            const taxable = Math.max(0, base - discount);
            subtotal += taxable;

            let cgstP = 0, sgstP = 0, igstP = 0, vatP = 0;
            let cgstA = 0, sgstA = 0, igstA = 0, vatA = 0;

            if (posTaxType === "gst") {
                if (useIntraState) {
                    cgstP = item.taxPercentage / 2;
                    sgstP = item.taxPercentage / 2;
                    cgstA = taxable * (cgstP / 100);
                    sgstA = taxable * (sgstP / 100);
                } else {
                    igstP = item.taxPercentage;
                    igstA = taxable * (igstP / 100);
                }
            } else {
                vatP = item.taxPercentage;
                vatA = taxable * (vatP / 100);
            }

            totalCGST += cgstA;
            totalSGST += sgstA;
            totalIGST += igstA;
            totalVAT += vatA;

            return {
                ...item,
                CGSTPercent: Number(cgstP.toFixed(2)),
                CGSTAmount: Number(cgstA.toFixed(2)),
                SGSTPercent: Number(sgstP.toFixed(2)),
                SGSTAmount: Number(sgstA.toFixed(2)),
                IGSTPercent: Number(igstP.toFixed(2)),
                IGSTAmount: Number(igstA.toFixed(2)),
                VATPercent: Number(vatP.toFixed(2)),
                VATAmount: Number(vatA.toFixed(2)),
                taxAmount: Number((cgstA + sgstA + igstA + vatA).toFixed(2)),
            };
        });

        setItems(prev => {
            if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
            return updated;
        });

        const totalTax = totalCGST + totalSGST + totalIGST + totalVAT;
        const grandTotal = subtotal + totalTax;

        setNetTotal(subtotal);
        setTotalPrice(grandTotal);
        setTaxSummary({
            CGST: Number(totalCGST.toFixed(2)),
            SGST: Number(totalSGST.toFixed(2)),
            IGST: Number(totalIGST.toFixed(2)),
            VAT: Number(totalVAT.toFixed(2)),
            totalTax: Number(totalTax.toFixed(2)),
        });

        if (!isTotalPaidModified) {
            setTotalPaid(grandTotal.toFixed(2));
        }
    }, [items, pharmacyStateCode, supplierStateCode, posTaxType, isTotalPaidModified]);

    useEffect(() => {
        recalculateAll();
    }, [recalculateAll]);

    // ============ VALIDATION ============
    const validateForm = () => {
        let newErrors = {};

        if (items.length === 0) newErrors.items = t("At least one item required");
        if (!totalPaid.trim()) newErrors.totalPaid = t("Total paid is required");

        const itemErrors = [];

        items.forEach((item, index) => {
            const errors = {};

            if (!item.isOTC && (!item.batchNo || item.batchNo.trim() === "")) {
                errors.batchNo = t("Required field");
            }

            if (!item.manufacturingDate || item.manufacturingDate.trim() === "") {
                errors.manufacturingDate = t("Required field");
            }

            if (item.unitPrice === 0) {
                errors.unitPrice = t("Required field");
            }

            if (item.unitSalePrice === 0) {
                errors.unitSalePrice = t("Required field");
            } else {
                const manufacturing = new Date(item.manufacturingDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (isNaN(manufacturing.getTime()) || manufacturing > today) {
                    errors.manufacturingDate = t("Invalid date");
                }
            }

            if (!item.expiryDate || item.expiryDate.trim() === "") {
                errors.expiryDate = t("Required field");
            } else {
                const expiry = new Date(item.expiryDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (isNaN(expiry.getTime()) || expiry <= today) {
                    errors.expiryDate = t("Invalid date");
                }
            }

            itemErrors.push(errors);
        });

        const hasItemErrors = itemErrors.some(err => Object.keys(err).length > 0);
        if (hasItemErrors) {
            newErrors.itemErrors = itemErrors;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ============ SUBMIT ============
    const handleCreatePurchase = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.warning(t("Please fill all the required fields"));
            return;
        }

        // Calculate payment status
        const totalAmount = Number(totalPrice);
        const paid = Number(totalPaid) || 0;
        const isPaid = paid >= totalAmount;
        const isPartialPaid = paid > 0 && paid < totalAmount;
        const isSettled = isPaid;

        const useIntraState = determineTaxType(pharmacyStateCode, supplierStateCode);

        const payload = {
            clinicId: pharmacyPOS?.hospital_id,
            pharmacyId: pharmacyPOS?._id,
            supplierName: supplier?.raw?.companyName || "",
            supplierId: supplier?.value || "",
            NetTotal: totalPrice.toFixed(2),
            taxableAmount: netTotal.toFixed(2),
            totalPaid: paid.toFixed(2),
            isPaid: isPaid,
            isPartialPaid: isPartialPaid,
            isSettled: isSettled,
            purchaseDate: date,
            notes: note,

            otherNetTotalPrice: 0,
            currencyType: currency,

            // Overall tax info
            taxPercentage: Math.max(...items.map(i => i.taxPercentage), 0),
            tax: taxSummary.totalTax.toFixed(2),
            isIGST: !useIntraState,
            IGST: taxSummary.IGST.toFixed(2),
            CGST: taxSummary.CGST.toFixed(2),
            SGST: taxSummary.SGST.toFixed(2),
            IGSTPercentage: !useIntraState ? Math.max(...items.map(i => i.taxPercentage), 0) : 0,
            CGSTPercentage: useIntraState ? (Math.max(...items.map(i => i.taxPercentage), 0) / 2) : 0,
            SGSTPercentage: useIntraState ? (Math.max(...items.map(i => i.taxPercentage), 0) / 2) : 0,

            stocks: items.map((item) => {
                const itemSubtotal = Number(item.stkQuantity) * Number(item.unitPrice) - Number(item.discount || 0);
                const totalPackingQty = Number(item.stkQuantity) * Number(item.packageQuantity);
                const totalPrice = Number(calculateItemTotalWithTax(item)) || 0;

                const calculatedUnitPrice =
                    totalPackingQty > 0
                        ? (itemSubtotal + Number(item.taxAmount || 0)) / totalPackingQty
                        : 0;

                const calculatedUnitPricePerMedicine =
                    totalPackingQty > 0
                        ? Number(item.unitSalePrice || 0) / Number(item.packageQuantity)
                        : 0;

                return {
                    itemId: item._id,
                    isOTC: item.isOTC,
                    code: item.code,
                    brandName: item.brandName,
                    medicineName: item.saltName || "",
                    manufacturerName: item.manufacturer,
                    batchNumber: item.batchNo,
                    manufacturingDate: item.manufacturingDate,
                    expiryDate: item.expiryDate,
                    package: item.package,
                    packing: item.packageQuantity,
                    quantity: item.stkQuantity,
                    taxableAmount: itemSubtotal.toFixed(2),
                    totalPrice,
                    totalMedicine: item.itemQuantity,

                    unitPrice: calculatedUnitPrice.toFixed(2),
                    unitPricePerMedicine: calculatedUnitPricePerMedicine.toFixed(2),
                    discount: Number(item.discount) || 0,

                    HSN: item.HSN || "123",
                    MFG: item.manufacturer || "",
                    MRP: Number(item.unitSalePrice).toFixed(2),
                    rate: Number(item.unitPrice).toFixed(2),

                    taxPercentage: item.taxPercentage,
                    tax: item.taxAmount.toFixed(2),
                    isIGST: !useIntraState,
                    IGST: item.IGSTAmount.toFixed(2),
                    CGST: item.CGSTAmount.toFixed(2),
                    SGST: item.SGSTAmount.toFixed(2),
                    IGSTPercentage: !useIntraState ? item.taxPercentage : 0,
                    CGSTPercentage: useIntraState ? item.taxPercentage / 2 : 0,
                    SGSTPercentage: useIntraState ? item.taxPercentage / 2 : 0,
                };
            }),

            // Link to pre-order
            preOrderId: preOrderData?._id,
            preOrderNumber: preOrderData?.orderNumber || preOrderData?._id,
        };

        console.log('Purchase from pre-order payload:', payload);

        try {
            setLoading(true);
            const res = await addStock(payload);
            toast.success(res.data.message || t("Purchase created successfully"));
            navigate(-1);
        } catch (err) {
            console.error("Error saving purchase:", err);
            toast.warning(err.response?.data?.message || t("Failed to create purchase"));
        } finally {
            setLoading(false);
        }
    };

    const isDark = document.body.classList.contains("dark-only");
    return (
        <Fragment>
            <Breadcrumbs
                mainTitle={t("Purchase Order")}
                parent={t("Dashboard")}
                title={t("Purchase Order")}
            />

            <Col sm="12">
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="mb-0">
                                    {t("Purchase Order")}
                                </h5>
                                {preOrderData && (
                                    <small className="text-muted">
                                        {t("Order ID")}: {preOrderData._id}
                                    </small>
                                )}
                            </div>
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate(-1)}
                            >
                                <FaArrowLeft className="me-2" /> {t("Back")}
                            </button>
                        </div>

                        {!preOrderData ? (
                            <div className="alert alert-danger">
                                {t("No pre-order data found. Please go back and try again.")}
                            </div>
                        ) : (
                            <Form onSubmit={handleCreatePurchase}>
                                <div className="row stock-adjustment">
                                    {/* Supplier (Read-only) */}
                                    <div className="col-md-6">
                                        <label className="form-label">{t("Supplier")} *</label>
                                        <Form.Control
                                            type="text"
                                            value={supplier?.label || t("Loading...")}
                                            disabled
                                        />
                                        {/* <small className="text-muted">
                                            {t("Auto-filled from pre-order")}
                                        </small> */}
                                    </div>

                                    {/* Add Additional Items */}
                                    <div className="col-md-6">
                                        <label className="form-label">{t("Add More Items")}</label>
                                        <AsyncSelect
                                            className="flex-grow-1"
                                            cacheOptions
                                            loadOptions={loadMedicines}
                                            defaultOptions={[]}
                                            onChange={handleSelectMedicine}
                                            value={selectedMedicine}
                                            placeholder={t("Search & add more items")}
                                            onInputChange={(newValue) => setSearchQuery(newValue)}
                                            noOptionsMessage={() =>
                                                searchQuery
                                                    ? t("No items found")
                                                    : t('Start typing to search...')
                                            }
                                            isClearable={true}
                                            isLoading={loading}
                                            theme={(theme) => ({
                                                ...theme,
                                                colors: {
                                                    ...theme.colors,
                                                    primary25: isDark ? "#373737" : "#e6f7ffff",

                                                },
                                            })}
                                        />
                                        <small className="text-muted">
                                            {t("Optional: Add items not in the pre-order")}
                                        </small>
                                    </div>

                                    {/* Items Table (Same as AddStock) */}
                                    <div className="col-md-12">
                                        {items.length > 0 && (
                                            <div className="mt-3 mb-3 table-responsive table-wrap stock-adjustment">
                                                <Table className="align-middle rounded shadow-sm table-bordered" bsSize="sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Brand")}
                                                                <InfoIcon text={COLUMN_INFO.brand} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Batch")}
                                                                <InfoIcon text={COLUMN_INFO.batch} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Mfg Date")}
                                                                <InfoIcon text={COLUMN_INFO.mfgDate} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Exp Date")}
                                                                <InfoIcon text={COLUMN_INFO.expDate} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Package")}
                                                                <InfoIcon text={COLUMN_INFO.package} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Pkg Qty")}
                                                                <InfoIcon text={COLUMN_INFO.pkgQty} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold">
                                                                {t("Total Unit Qty")}
                                                                <InfoIcon text={COLUMN_INFO.totalUnitQty} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-wrap">
                                                                {t("Regular Price")}
                                                                <InfoIcon text={COLUMN_INFO.regularPrice} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("PKG Rate")}
                                                                <InfoIcon text={COLUMN_INFO.pkgRate} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Discount")}
                                                                <InfoIcon text={COLUMN_INFO.discount} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold">
                                                                {t("Gross value")}
                                                                <InfoIcon text={COLUMN_INFO.grossValue} />
                                                            </th>

                                                            {/* TAX GROUP */}
                                                            <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">
                                                                {t("Tax")}
                                                                <InfoIcon text={COLUMN_INFO.tax} />
                                                            </th>

                                                            {/* GST Groups */}
                                                            {posTaxType === "gst" ? (
                                                                isSameState ? (
                                                                    <>
                                                                        <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">
                                                                            {t("CGST")}
                                                                        </th>
                                                                        <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">
                                                                            {t("SGST")}
                                                                        </th>
                                                                    </>
                                                                ) : (
                                                                    <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">
                                                                        {t("IGST")}
                                                                    </th>
                                                                )
                                                            ) : (
                                                                <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">
                                                                    {t("VAT")}
                                                                </th>
                                                            )}
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("PKG MRP")}
                                                                <InfoIcon text={COLUMN_INFO.pkgMrp} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">
                                                                {t("Subtotal")}
                                                                <InfoIcon text={COLUMN_INFO.subtotal} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold">
                                                                {t("Action")}
                                                            </th>
                                                        </tr>

                                                        {/* SECOND ROW — Sub-headings */}
                                                        <tr>
                                                            <th className="bg-light-primary text-dark">
                                                                {posTaxType === "gst" ? t("GST") : t("VAT")} %
                                                            </th>
                                                            <th className="bg-light-primary text-dark">{t("Amt")}</th>

                                                            {posTaxType === "gst" ? (
                                                                isSameState ? (
                                                                    <>
                                                                        <th className="bg-light-primary text-dark">%</th>
                                                                        <th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                        <th className="bg-light-primary text-dark">%</th>
                                                                        <th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <th className="bg-light-primary text-dark">%</th>
                                                                        <th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                    </>
                                                                )
                                                            ) : (
                                                                <>
                                                                    <th className="bg-light-primary text-dark">%</th>
                                                                    <th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                </>
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {items.map((item, index) => (
                                                            <tr key={item._id || index}>
                                                                <td className="border-b-2" style={{ maxWidth: "130px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip id={`tooltip-${item._id}`}>{item.brandName}</Tooltip>}
                                                                    >
                                                                        <span>{item.brandName}</span>
                                                                    </OverlayTrigger>
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        value={item.batchNo || ""}
                                                                        className="form-control form-control-sm"
                                                                        placeholder={t("batch")}
                                                                        onChange={(e) => updateBatchNo(item._id, e.target.value)}
                                                                    />
                                                                    {errors.itemErrors?.[index]?.batchNo && (
                                                                        <small className="text-danger">{errors.itemErrors[index].batchNo}</small>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="date"
                                                                        value={item.manufacturingDate || ""}
                                                                        className="form-control form-control-sm"
                                                                        max={new Date().toISOString().split("T")[0]}
                                                                        onChange={(e) => updateManufacturingDate(item._id, e.target.value)}
                                                                    />
                                                                    {errors.itemErrors?.[index]?.manufacturingDate && (
                                                                        <small className="text-danger">{errors.itemErrors[index].manufacturingDate}</small>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="date"
                                                                        value={item.expiryDate || ""}
                                                                        className="form-control form-control-sm"
                                                                        min={item.manufacturingDate || new Date().toISOString().split("T")[0]}
                                                                        onChange={(e) => updateExpiryDate(item._id, e.target.value)}
                                                                    />
                                                                    {errors.itemErrors?.[index]?.expiryDate && (
                                                                        <small className="text-danger">{errors.itemErrors[index].expiryDate}</small>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">{item.package} ({item.packageQuantity})</td>
                                                                <td>
                                                                    <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                                                        <button
                                                                            type="button"
                                                                            className="button-minus icon-shape icon-sm btn-danger"
                                                                            onClick={() => updatePackageQty(item._id, item.stkQuantity - 1)}
                                                                        >
                                                                            -
                                                                        </button>
                                                                        <input
                                                                            type="number"
                                                                            value={item.stkQuantity}
                                                                            className="border-0 quantity-field incrementor"
                                                                            onChange={(e) => updatePackageQty(item._id, e.target.value)}
                                                                            {...disableScrollProps}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="button-plus icon-shape icon-sm btn-primary"
                                                                            onClick={() => updatePackageQty(item._id, item.stkQuantity + 1)}
                                                                        >
                                                                            +
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                                <td className="text-center">{item.itemQuantity}</td>
                                                                <td>{getCurrencySymbol()}{item.regularPrice}</td>
                                                                <td>
                                                                    <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                                                        <button
                                                                            type="button"
                                                                            className="button-minus icon-shape icon-sm btn-danger"
                                                                            onClick={() => updateItem(item._id, "unitPrice", Math.max(0, item.unitPrice - 1))}
                                                                        >
                                                                            -
                                                                        </button>
                                                                        <input
                                                                            type="number"
                                                                            value={item.unitPrice}
                                                                            className="border-0 quantity-field incrementor"
                                                                            onChange={(e) => updateItem(item._id, "unitPrice", e.target.value)}
                                                                            {...disableScrollProps}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="button-plus icon-shape icon-sm btn-primary"
                                                                            onClick={() => updateItem(item._id, "unitPrice", item.unitPrice + 1)}
                                                                        >
                                                                            +
                                                                        </button>
                                                                    </div>
                                                                    {errors.itemErrors?.[index]?.unitPrice && (
                                                                        <small className="text-danger">{errors.itemErrors[index].unitPrice}</small>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        bsSize="sm"
                                                                        value={item.discount || ""}
                                                                        onChange={(e) => updateItem(item._id, "discount", e.target.value)}
                                                                    />
                                                                </td>
                                                                <td className="text-success fw-bold">
                                                                    {getCurrencySymbol()}{calculateSubtotal(item)}
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        bsSize="sm"
                                                                        value={item.taxPercentage}
                                                                        onChange={(e) => updateItem(item._id, "taxPercentage", Number(e.target.value))}
                                                                    />
                                                                </td>
                                                                <td className="text-success">
                                                                    {getCurrencySymbol()}{item.taxAmount?.toFixed(2)}
                                                                </td>

                                                                {posTaxType === "gst" ? (
                                                                    isSameState ? (
                                                                        <>
                                                                            <td className="text-muted">
                                                                                {(item.taxPercentage / 2).toFixed(2)}
                                                                            </td>
                                                                            <td className="text-success">
                                                                                {getCurrencySymbol()}{item.CGSTAmount.toFixed(2)}
                                                                            </td>
                                                                            <td className="text-muted">
                                                                                {(item.taxPercentage / 2).toFixed(2)}
                                                                            </td>
                                                                            <td className="text-success">
                                                                                {getCurrencySymbol()}{item.SGSTAmount.toFixed(2)}
                                                                            </td>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <td className="text-muted">
                                                                                {item.taxPercentage.toFixed(2)}
                                                                            </td>
                                                                            <td className="text-success">
                                                                                {getCurrencySymbol()}{item.IGSTAmount.toFixed(2)}
                                                                            </td>
                                                                        </>
                                                                    )
                                                                ) : (
                                                                    <>
                                                                        <td className="text-muted">
                                                                            {item.taxPercentage.toFixed(2)}
                                                                        </td>
                                                                        <td className="text-success">
                                                                            {getCurrencySymbol()}{item.VATAmount.toFixed(2)}
                                                                        </td>
                                                                    </>
                                                                )}
                                                                <td>
                                                                    <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                                                        <button
                                                                            type="button"
                                                                            className="button-minus icon-shape icon-sm btn-danger"
                                                                            onClick={() => updateItem(item._id, "unitSalePrice", Math.max(0, item.unitSalePrice - 1))}
                                                                        >
                                                                            -
                                                                        </button>
                                                                        <input
                                                                            type="number"
                                                                            value={item.unitSalePrice}
                                                                            className="border-0 quantity-field incrementor"
                                                                            onChange={(e) => updateItem(item._id, "unitSalePrice", e.target.value)}
                                                                            {...disableScrollProps}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="button-plus icon-shape icon-sm btn-primary"
                                                                            onClick={() => updateItem(item._id, "unitSalePrice", item.unitSalePrice + 1)}
                                                                        >
                                                                            +
                                                                        </button>
                                                                    </div>
                                                                    {errors.itemErrors?.[index]?.unitSalePrice && (
                                                                        <small className="text-danger">{errors.itemErrors[index].unitSalePrice}</small>
                                                                    )}
                                                                </td>
                                                                <td className="text-success fw-bold">
                                                                    {getCurrencySymbol()}{calculateItemTotalWithTax(item)}
                                                                </td>
                                                                <td className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-danger btn-sm remove-item"
                                                                        onClick={() => removeItem(item._id)}
                                                                    >
                                                                        &times;
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}

                                                        {/* Totals Row */}
                                                        <tr>
                                                            <td colSpan="5">
                                                                <strong>{t("Sub Totals")}:</strong>
                                                            </td>
                                                            <td className="text-center">
                                                                {items.reduce((acc, item) => acc + Number(item.stkQuantity), 0)}
                                                            </td>
                                                            <td className="text-center">
                                                                {items.reduce((acc, item) => acc + Number(item.itemQuantity), 0)}
                                                            </td>
                                                            <td></td>
                                                            <td></td>
                                                            <td className="text-success fw-bold">
                                                                {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.discount || 0), 0).toFixed(2)}
                                                            </td>
                                                            <td className="text-success fw-bold">
                                                                {getCurrencySymbol()}{netTotal.toFixed(2)}
                                                            </td>
                                                            <td></td>
                                                            <td className="text-success fw-bold">
                                                                {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.taxAmount || 0), 0).toFixed(2)}
                                                            </td>
                                                            {posTaxType === "gst" ? (
                                                                isSameState ? (
                                                                    <>
                                                                        <td></td>
                                                                        <td className="text-success fw-bold">
                                                                            {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.CGSTAmount || 0), 0).toFixed(2)}
                                                                        </td>
                                                                        <td></td>
                                                                        <td className="text-success fw-bold">
                                                                            {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.SGSTAmount || 0), 0).toFixed(2)}
                                                                        </td>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <td></td>
                                                                        <td className="text-success fw-bold">
                                                                            {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.IGSTAmount || 0), 0).toFixed(2)}
                                                                        </td>
                                                                    </>
                                                                )
                                                            ) : (
                                                                <>
                                                                    <td></td>
                                                                    <td className="text-success fw-bold">
                                                                        {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.VATAmount || 0), 0).toFixed(2)}
                                                                    </td>
                                                                </>
                                                            )}
                                                            <td></td>
                                                            <td className="text-success fw-bold">
                                                                {getCurrencySymbol()}{totalPrice.toFixed(2)}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>

                                    {/* Summary Section */}
                                    {items.length > 0 && (
                                        <div className="row mt-5">
                                            {/* GST Rate-wise Summary */}
                                            <div className="col-md-8">
                                                <Table bordered bsSize="sm" className="mb-4">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>{t("GST %")}</th>
                                                            <th className="text-end">{t("Taxable Amt")}</th>
                                                            {posTaxType === "gst" && isSameState ? (
                                                                <>
                                                                    <th className="text-end">{t("CGST Amt")}</th>
                                                                    <th className="text-end">{t("SGST Amt")}</th>
                                                                </>
                                                            ) : (
                                                                <th className="text-end">{t("IGST Amt")}</th>
                                                            )}
                                                            <th className="text-end">{t("Tax Amt")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.entries(
                                                            items.reduce((acc, item) => {
                                                                const rate = item.taxPercentage;
                                                                if (!acc[rate]) {
                                                                    acc[rate] = {
                                                                        taxable: 0,
                                                                        cgst: 0,
                                                                        sgst: 0,
                                                                        igst: 0,
                                                                        totalTax: 0,
                                                                    };
                                                                }
                                                                const taxable = Number(item.stkQuantity) * Number(item.unitPrice) - Number(item.discount || 0);
                                                                acc[rate].taxable += taxable;
                                                                acc[rate].cgst += item.CGSTAmount;
                                                                acc[rate].sgst += item.SGSTAmount;
                                                                acc[rate].igst += item.IGSTAmount;
                                                                acc[rate].totalTax += item.taxAmount;
                                                                return acc;
                                                            }, {})
                                                        )
                                                            .sort(([a], [b]) => a - b)
                                                            .map(([rate, data]) => (
                                                                <tr key={rate}>
                                                                    <td className="text-center fw-bold">{rate}%</td>
                                                                    <td className="text-end">{getCurrencySymbol()}{data.taxable.toFixed(2)}</td>
                                                                    {posTaxType === "gst" && isSameState ? (
                                                                        <>
                                                                            <td className="text-end text-success">
                                                                                {getCurrencySymbol()}{data.cgst.toFixed(2)}
                                                                            </td>
                                                                            <td className="text-end text-success">
                                                                                {getCurrencySymbol()}{data.sgst.toFixed(2)}
                                                                            </td>
                                                                        </>
                                                                    ) : (
                                                                        <td className="text-end text-success">
                                                                            {getCurrencySymbol()}{data.igst.toFixed(2)}
                                                                        </td>
                                                                    )}
                                                                    <td className="text-end text-success">
                                                                        {getCurrencySymbol()}{data.totalTax.toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        <tr>
                                                            <td colSpan="2"></td>
                                                            {posTaxType === "gst" && isSameState && (
                                                                <>
                                                                    <td className="text-end">
                                                                        {getCurrencySymbol()}{taxSummary.CGST.toFixed(2)}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {getCurrencySymbol()}{taxSummary.SGST.toFixed(2)}
                                                                    </td>
                                                                </>
                                                            )}
                                                            {posTaxType === "gst" && !isSameState && (
                                                                <td className="text-end">
                                                                    {getCurrencySymbol()}{taxSummary.IGST.toFixed(2)}
                                                                </td>
                                                            )}
                                                            {posTaxType !== "gst" && (
                                                                <td className="text-end text-success w-25">
                                                                    {getCurrencySymbol()}{taxSummary.VAT.toFixed(2)}
                                                                </td>
                                                            )}
                                                            <td className="text-end">
                                                                {getCurrencySymbol()}{taxSummary.totalTax.toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Final Tax Addition & Grand Total */}
                                            <div className="col-md-4">
                                                <div className="row align-items-center justify-content-between">
                                                    <div className="col-8">
                                                        <p>{t("Total Taxable Amount")}:</p>
                                                    </div>
                                                    <div className="col-4 text-end">
                                                        <span className="fw-bold">
                                                            {getCurrencySymbol()}{netTotal.toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <Table bordered className="table-sm">
                                                    <tbody>
                                                        {posTaxType === "gst" && isSameState && (
                                                            <>
                                                                <tr>
                                                                    <td className="text-end">Add: CGST</td>
                                                                    <td className="text-end text-success w-25">
                                                                        {getCurrencySymbol()}{taxSummary.CGST.toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-end">Add: SGST</td>
                                                                    <td className="text-end text-success w-25">
                                                                        {getCurrencySymbol()}{taxSummary.SGST.toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                            </>
                                                        )}
                                                        {posTaxType === "gst" && !isSameState && (
                                                            <tr>
                                                                <td className="text-end">Add: IGST</td>
                                                                <td className="text-end text-success w-25">
                                                                    {getCurrencySymbol()}{taxSummary.IGST.toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        )}
                                                        {posTaxType !== "gst" && (
                                                            <tr>
                                                                <td className="text-end">Add: VAT</td>
                                                                <td className="text-end text-success w-25">
                                                                    {getCurrencySymbol()}{taxSummary.VAT.toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        )}
                                                        <tr className="table-primary">
                                                            <td className="text-end fw-bold">{t("Total Amount")}</td>
                                                            <td className="text-end fw-bold fs-5 text-primary">
                                                                {getCurrencySymbol()}{totalPrice.toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Total Paid & Date */}
                                            <div className="row justify-content-end mt-4">
                                                <div className="col-6">
                                                    <div className="row g-3">
                                                        <div className="col-6">
                                                            <Form.Group>
                                                                <Form.Label className="fw-bold">
                                                                    {t("Total Paid")} *
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    value={totalPaid}
                                                                    onChange={(e) => {
                                                                        setTotalPaid(e.target.value);
                                                                        setIsTotalPaidModified(true);
                                                                    }}
                                                                    className="text-end fw-bold"
                                                                    style={{ fontSize: "1.1rem" }}
                                                                />
                                                            </Form.Group>
                                                        </div>
                                                        <div className="col-6">
                                                            <Form.Group>
                                                                <Form.Label>{t("Purchase Date")}</Form.Label>
                                                                <Form.Control
                                                                    type="date"
                                                                    value={date}
                                                                    onChange={(e) => setDate(e.target.value)}
                                                                    max={new Date().toISOString().split("T")[0]}
                                                                />
                                                            </Form.Group>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Note */}
                                            <div className="mt-4">
                                                <Form.Group>
                                                    <Form.Label>{t("Note")}</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={note}
                                                        onChange={(e) => setNote(e.target.value)}
                                                        placeholder={t("Any additional notes...")}
                                                    />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="d-flex justify-content-end gap-3 mt-4">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={loading || items.length === 0}
                                        >
                                            {loading ? t("Saving...") : t("Create Purchase")}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => navigate(-1)}
                                        >
                                            {t("Cancel")}
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </CardBody>
                </Card>
            </Col>
        </Fragment>
    );
};

export default PurchaseFromPreOrder;